import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getCurrentSession = createAsyncThunk('sessions/getCurrentSession', async (_, { rejectWithValue }) => {
  const token = TokenUtils.getToken();

  try {
    const response = await api.get('/sessions/current', {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Немає активної сесії');
  }
});

export const getUserSessions = createAsyncThunk(
  'sessions/getUserSessions',
  async ({ userID, params = {} }, { rejectWithValue }) => {
    const token = TokenUtils.getToken();

    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await api.get(`/sessions/user/${userID}?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати сесії користувача');
    }
  }
);

export const getAllSessions = createAsyncThunk('sessions/getAllSessions', async (params = {}, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.userID) queryParams.append('userID', params.userID);

    const response = await api.get(`/sessions?${queryParams}`);

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати всі сесії');
  }
});

export const calculateWorkingTime = createAsyncThunk(
  'sessions/calculateWorkingTime',
  async ({ userID, params }, { rejectWithValue }) => {
    const token = TokenUtils.getToken();

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('startDate', params.startDate);
      queryParams.append('endDate', params.endDate);

      const response = await api.get(`/sessions/work-time/${userID}?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося розрахувати робочий час');
    }
  }
);

export const getWorkTimeReport = createAsyncThunk('sessions/getWorkTimeReport', async (params, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    const response = await api.get(`/sessions/work-time-report?${queryParams}`);

    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати звіт по робочому часу');
  }
});

export const closeActiveSession = createAsyncThunk(
  'sessions/closeActiveSession',
  async (sessionID, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.patch(`/sessions/close/${sessionID}`);

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Сесію закрито',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося закрити сесію');
    }
  }
);

export const getActiveUsersStats = createAsyncThunk('sessions/getActiveUsersStats', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/sessions/stats/active-users');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати статистику активних користувачів');
  }
});

const sessionSlice = createSlice({
  name: 'sessions',
  initialState: {
    sessions: [],
    currentSession: null,
    workTime: null,
    workTimeReport: { period: null, report: [], totalEmployees: 0, totalWorkingMinutes: 0, totalWorkingHours: '0.00' },
    activeUsersStats: null,
    isLoading: false,
    error: null,
    meta: {
      totalCount: 0,
      currentPage: 1,
      totalPages: 0,
    },
  },
  reducers: {
    updateSession: (state, action) => {
      const foundSessionIndex = state.sessions.findIndex((s) => s.id === action.payload.id);

      if (foundSessionIndex !== -1) {
        state.sessions[foundSessionIndex] = action.payload;
      } else {
        state.sessions.unshift(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentSession.fulfilled, (state, action) => {
        state.currentSession = action.payload.data || null;
        state.error = null;
      })
      .addCase(getCurrentSession.rejected, (state, action) => {
        state.currentSession = null;
        state.error = action.payload;
      })
      .addCase(getUserSessions.fulfilled, (state, action) => {
        const { data, meta } = action.payload;

        state.sessions = data;
        state.meta = meta;

        state.error = null;
      })
      .addCase(getUserSessions.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(calculateWorkingTime.fulfilled, (state, action) => {
        state.workTime = action.payload;
        state.error = null;
      })
      .addCase(calculateWorkingTime.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getWorkTimeReport.fulfilled, (state, action) => {
        state.workTimeReport = {
          period: action.payload.period ?? null,
          report: action.payload.report || [],
          totalEmployees: action.payload.totalEmployees ?? 0,
          totalWorkingMinutes: action.payload.totalWorkingMinutes ?? 0,
          totalWorkingHours: action.payload.totalWorkingHours ?? '0.00',
        };
        state.error = null;
      })
      .addCase(getWorkTimeReport.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(closeActiveSession.fulfilled, (state, action) => {
        const closedID = action.meta.arg;
        if (state.sessions?.length) {
          state.sessions = state.sessions.filter((s) => s.id !== closedID);
          if (state.meta.totalCount > 0) state.meta.totalCount -= 1;
        }
        state.error = null;
      })
      .addCase(closeActiveSession.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(getActiveUsersStats.fulfilled, (state, action) => {
        state.activeUsersStats = action.payload;
        state.error = null;
      })
      .addCase(getActiveUsersStats.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const { updateSession } = sessionSlice.actions;
export default sessionSlice.reducer;
